import { Box, Button, Grid } from '@mui/material';
import useAnotherDataForm, { AnotherDataFormData } from './hooks/useAnotherDataForm';
import useEmployeeQuantityForm, { EmployeeQuantityFormData } from './hooks/useEmployeeQuantityForm';
import { useTranslation } from 'react-i18next';
import SectionFormBox from '../../../../components/SectionFormBox/SectionFormBox';
import CustomField from '../../../../components/CustomFormsComponents/CustomField';
import useEstablishmentModule from './hooks/useEstablishmentModule';
import { useAppSelector } from '../../../../hooks/reduxHooks';
import { TEST_IDS } from './testIds';
import { TAB_INDEX } from '../../pages';
import { CarcinogenicModuleFooter } from '../../components/CarcinogenicModuleFooter';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import { useNavigate } from 'react-router-dom';

const EstablishmentDataTabContent = ({ handleTabChange, showCancelCarcinogenicReportAlert, onlyVisual }: any) => {
    const navigate = useNavigate();
    const carcinogenicReportData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicReport
    );

    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const selectedEstablishmentId = useAppSelector(
        (state) => state.establishment.selectedEstablishmentId
    );

    const handleFooterCancelClick = () => {
        if (onlyVisual) return;
        !selectedCarcinogenicReportId
            ? navigate(-1)
            : showCancelCarcinogenicReportAlert({ confirmAction: () => navigate(-1) });
    }

    const handleFooterNextClick = () => {
         if (
             selectedCarcinogenicReportId &&
             !hasEmployeeQuantityFormChanged &&
             !hasAnotherDataFormChanged
         ) {
             handleTabChange(null, TAB_INDEX.SUBSTANCES);
         } else {
             handleSubmit();
         }
    }
    

    const { t } = useTranslation('carcinogenicReport', { keyPrefix: 'modules.establishmentData' });

    const employeeQuantity = useEmployeeQuantityForm(carcinogenicReportData);
    const anotherData = useAnotherDataForm(carcinogenicReportData);

    const {
        formData: employeeQuantityFormData,
        errors: employeeQuantityErrors,
        hasFormDataChanged: hasEmployeeQuantityFormChanged,
        employeeQuantityFieldsConfig,
        handleChange: handleChangeEmployeeQuantity
    } = employeeQuantity;

    const {
        formData: anotherDataFormData,
        errors: anotherDataErrors,
        hasFormDataChanged: hasAnotherDataFormChanged,
        anotherDataFieldsConfig,
        handleChange: handleChangeAnotherData
    } = anotherData;

    const {
        handleSubmit,
        handleCancelPress,
        isCreatingCarcinogenicReport,
        isEditingCarcinogenicReport,
        isUpdatingPresentationState
    } = useEstablishmentModule({
        employeeQuantity,
        anotherData,
        selectedEstablishmentId,
        handleTabChange
    });

    return (
        <>
            <Box
                component="div"
                justifyContent="center"
                bgcolor={'#ffffff'}
                borderRadius={'0px 0px 6px 6px'}
                pl={2}
                pr={2}
            >
                <SectionFormBox
                    title={t('workersQuantityForm.title')}
                    testID={TEST_IDS.containers.employeeQuantityFields}
                >
                    {employeeQuantityFieldsConfig.map((field, index) => (
                        <Grid
                            item
                            xs={field?.gridSpace?.xs}
                            sm={field?.gridSpace?.sm}
                            md={field?.gridSpace?.md}
                            lg={field?.gridSpace?.lg}
                            key={index}
                        >
                            <CustomField
                                config={field}
                                onlyVisual={onlyVisual}
                                value={
                                    employeeQuantityFormData[
                                        field.name as keyof EmployeeQuantityFormData
                                    ]
                                }
                                onChange={handleChangeEmployeeQuantity}
                                error={employeeQuantityErrors[field.name as string]?.hasError}
                                helperText={
                                    employeeQuantityErrors[field.name as string]?.errorMessage ||
                                    field.helperText
                                }
                            />
                        </Grid>
                    ))}
                </SectionFormBox>
                <SectionFormBox
                    title={t('anotherDataForm.title')}
                    testID={TEST_IDS.containers.anotherDataFields}
                >
                    {anotherDataFieldsConfig.map((field, index) => (
                        <Grid
                            item
                            xs={field?.gridSpace?.xs}
                            sm={field?.gridSpace?.sm}
                            md={field?.gridSpace?.md}
                            lg={field?.gridSpace?.lg}
                            key={index}
                        >
                            <CustomField
                                config={field}
                                onlyVisual={onlyVisual}
                                value={anotherDataFormData[field.name as keyof AnotherDataFormData]}
                                onChange={handleChangeAnotherData}
                                error={anotherDataErrors[field.name as string]?.hasError}
                                helperText={
                                    anotherDataErrors[field.name as string]?.errorMessage ||
                                    field.helperText
                                }
                            />
                        </Grid>
                    ))}
                </SectionFormBox>
                <BackdropComponent
                    loading={
                        isCreatingCarcinogenicReport ||
                        isEditingCarcinogenicReport ||
                        isUpdatingPresentationState
                    }
                />
            </Box>
            <CarcinogenicModuleFooter
                handleCancelClick={handleFooterCancelClick}
                handleSubmitClick={handleFooterNextClick}
                onlyVisual={onlyVisual}
                disableCancelButton={onlyVisual}
            />
        </>
    );
};

export default EstablishmentDataTabContent;
